import React, { useState } from 'react';

import { Popover } from 'antd';
import { Check, Filter } from 'lucide-react';

export interface ProjectFilterOption {
  value: string;
  label: string;
}

interface ProjectNameFilterPopoverProps {
  value: string;
  options: ProjectFilterOption[];
  disabled?: boolean;
  optionsLoading?: boolean;
  onChange: (value: string) => void;
}

const ProjectNameFilterPopover: React.FC<ProjectNameFilterPopoverProps> = ({
  value,
  options,
  disabled = false,
  optionsLoading = false,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const hasAppliedFilter = Boolean(value);
  const filterOptions = [{ value: '', label: '全部' }, ...options];

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) return;
    setOpen(nextOpen);
  };

  const handleSelect = (optionValue: string) => {
    if (disabled || optionsLoading) return;
    if (optionValue !== value) {
      onChange(optionValue);
    }
    setOpen(false);
  };

  const content = (
    <div className="w-64" data-testid="points-history-project-filter-panel">
      <div className="max-h-60 overflow-y-auto p-2" role="radiogroup" aria-label="按项目筛选">
        {optionsLoading ? (
          <div className="space-y-2 px-2 py-1.5" aria-label="项目列表加载中">
            <div className="skeleton-block h-8 rounded-lg" />
            <div className="skeleton-block h-8 rounded-lg" />
            <div className="skeleton-block h-8 rounded-lg" />
          </div>
        ) : (
          filterOptions.map((option) => {
            const selected = value === option.value;
            return (
              <label
                key={option.value}
                className={`flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-base ${
                  selected
                    ? 'bg-theme-color-10 text-theme-color'
                    : 'text-dark-text hover:bg-theme-color-10'
                }`}
              >
                <input
                  type="radio"
                  name="points-history-project-filter"
                  value={option.value}
                  checked={selected}
                  onChange={() => handleSelect(option.value)}
                  className="peer sr-only"
                />
                <span
                  aria-hidden="true"
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-base peer-focus-visible:ring-2 peer-focus-visible:ring-theme-color/30 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-dark-card ${
                    selected
                      ? 'border-theme-color bg-theme-color text-white'
                      : 'border-dark-text-secondary/50 bg-dark-bg text-transparent'
                  }`}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="min-w-0 flex-1 truncate" title={option.label}>
                  {option.label}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      trigger="click"
      placement="bottom"
      arrow={false}
      destroyOnHidden
      content={content}
      onOpenChange={handleOpenChange}
      classNames={{ root: 'points-history-project-filter-popover' }}
    >
      <button
        type="button"
        disabled={disabled}
        data-testid="points-history-project-filter"
        aria-label={hasAppliedFilter ? '按项目筛选，已筛选' : '按项目筛选'}
        aria-expanded={open}
        aria-busy={optionsLoading}
        className={`relative inline-flex h-3 w-3 shrink-0 items-center justify-center transition-colors duration-base after:absolute after:-inset-3.5 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-color/30 disabled:cursor-not-allowed disabled:opacity-50 ${
          hasAppliedFilter ? 'text-theme-color' : 'text-dark-text-secondary'
        }`}
      >
        <Filter className="h-3 w-3" strokeWidth={1.8} aria-hidden="true" />
      </button>
    </Popover>
  );
};

export default ProjectNameFilterPopover;
